import { useEffect, useRef, useState } from "react";

function FooterMap() {
  const mapWrapperRef = useRef(null);
  const mapRef = useRef(null);
  const isMapInitializedRef = useRef(false);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    if (!mapWrapperRef.current || isMapVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsMapVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );

    observer.observe(mapWrapperRef.current);

    return () => observer.disconnect();
  }, [isMapVisible]);

  useEffect(() => {
    if (!isMapVisible) {
      return;
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey || !mapRef.current) {
      return;
    }

    if (window.google?.maps) {
      initMap();
      return;
    }

    const existingScript = document.querySelector(
      'script[data-google-maps="footer-map"]',
    );

    if (existingScript) {
      if (window.google?.maps) {
        initMap();
        return;
      }

      existingScript.addEventListener("load", initMap);
      existingScript.addEventListener("error", handleMapError);
      return () => {
        existingScript.removeEventListener("load", initMap);
        existingScript.removeEventListener("error", handleMapError);
      };
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = "footer-map";
    script.addEventListener("load", initMap);
    script.addEventListener("error", handleMapError);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", initMap);
      script.removeEventListener("error", handleMapError);
    };
  }, [isMapVisible]);

  function handleMapError() {
    setMapError(true);
  }

  function initMap() {
    if (
      !mapRef.current ||
      !window.google?.maps ||
      isMapInitializedRef.current
    ) {
      return;
    }

    isMapInitializedRef.current = true;

    const branchAddress = "Baakenallee 90, 20457 Hamburg, Deutschland";
    const branchPoint = { lat: 53.5365685, lng: 10.0168628 };

    const map = new window.google.maps.Map(mapRef.current, {
      center: branchPoint,
      zoom: 17,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      clickableIcons: false,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#f3f3f3" }] },
        { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#747474" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#f8f8f8" }] },
        {
          featureType: "administrative",
          elementType: "geometry",
          stylers: [{ color: "#d8d8d8" }],
        },
        {
          featureType: "poi",
          elementType: "geometry",
          stylers: [{ color: "#ebebeb" }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#ffffff" }],
        },
        {
          featureType: "road.arterial",
          elementType: "geometry",
          stylers: [{ color: "#ffffff" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [{ color: "#dadada" }],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [{ color: "#e8e8e8" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#dcdcdc" }],
        },
      ],
    });

    const markerSvg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="23" height="32" viewBox="0 0 23 32" fill="none"><path d="M11.2804 0C5.04802 0 1.57223e-05 5.0508 1.57223e-05 11.2804C-0.00405471 13.7135 0.782338 16.0821 2.24082 18.0296L9.47642 30.5624C9.51842 30.6432 9.56042 30.7236 9.61201 30.7976L9.62802 30.8252L9.63242 30.8228C9.8207 31.0853 10.0691 31.299 10.3569 31.4459C10.6446 31.5928 10.9633 31.6687 11.2864 31.6672C11.9088 31.6672 12.4512 31.384 12.8244 30.9476L12.8432 30.9584L12.9148 30.8344C13.0204 30.692 13.114 30.54 13.1816 30.3724L20.2704 18.0932C21.7598 16.1346 22.5645 13.741 22.5608 11.2804C22.5608 5.0508 17.5128 0 11.2804 0ZM11.1688 16.9992C8.09802 16.9992 5.61082 14.5108 5.61082 11.4412C5.61082 8.3716 8.09802 5.8832 11.1688 5.8832C14.2396 5.8832 16.7268 8.372 16.7268 11.4412C16.7268 14.5104 14.2396 16.9992 11.1688 16.9992Z" fill="#00539c"/></svg>';

    const markerUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`;

    const createMarkerStyle = ({ width, height, lift = 0 }) => ({
      url: markerUrl,
      scaledSize: new window.google.maps.Size(width, height),
      anchor: new window.google.maps.Point(width / 2, height - lift),
      labelOrigin: new window.google.maps.Point(width / 2, -8 - lift),
    });

    const markerStyle = createMarkerStyle({ width: 23, height: 32 });
    const markerHoverStyle = createMarkerStyle({
      width: 28,
      height: 39,
      lift: 4,
    });

    const openGermanGoogleMaps = (address) => {
      const query = encodeURIComponent(address);
      window.open(
        `https://maps.app.goo.gl/mXEXr2XKBNq19vMX9`,
        "_blank",
        "noopener,noreferrer",
      );
    };

    const marker = new window.google.maps.Marker({
      map,
      position: branchPoint,
      icon: markerStyle,
      title: "Baakenallee 90",
      label: {
        text: "Baakenallee 90",
        color: "#00539c",
        fontSize: "12px",
        fontWeight: "600",
      },
    });

    const attachHoverEffect = (marker) => {
      marker.addListener("mouseover", () => {
        marker.setIcon(markerHoverStyle);
        marker.setZIndex(1000);
      });

      marker.addListener("mouseout", () => {
        marker.setIcon(markerStyle);
        marker.setZIndex(undefined);
      });
    };

    attachHoverEffect(marker);
    marker.addListener("click", () => openGermanGoogleMaps(branchAddress));

    setIsMapReady(true);
  }

  return (
    <div ref={mapWrapperRef} data-aos="fade-up" className="footer_map_wrapper">
      <div
        ref={mapRef}
        className={`footer_map_canvas ${isMapReady ? "is-ready" : ""}`}
      />
      {!isMapReady && process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
        <div className="footer_map_placeholder" aria-hidden="true" />
      )}
      {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
        <p className="footer_map_notice">
          Add REACT_APP_GOOGLE_MAPS_API_KEY to enable the map.
        </p>
      )}
      {mapError && (
        <p className="footer_map_notice">
          Map failed to load. Check API key restrictions and billing in Google
          Cloud.
        </p>
      )}
    </div>
  );
}

export default FooterMap;
